import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { reviewerStatementService } from '../../services/api/reviewerStatementService';
import StatementReviewDialog from './StatementReviewDialog';

const ResizableTableCell = ({ children, width, onResize, ...props }) => (
  <TableCell
    {...props}
    sx={{
      width: width,
      minWidth: width,
      maxWidth: width,
      position: 'relative',
      padding: '8px',
      ...props.sx
    }}
  >
    {children}
    {onResize && (
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '5px',
          cursor: 'col-resize',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3
          }
        }}
      />
    )}
  </TableCell>
);

const StatementReviewQueue = () => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    incidentRef: 120,
    statementId: 140,
    email: 200,
    handle: 120,
    accountId: 180,
    submittedAt: 150
  });
  const [resizing, setResizing] = useState(null);

  useEffect(() => {
    loadStatements();
  }, []);

  const loadStatements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reviewerStatementService.listStatementsForReview();
      setStatements(response.statements || []);
    } catch (err) {
      console.error('Error loading statements:', err);
      setError(err.message || 'Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (statement) => {
    setSelectedStatement(statement);
    setReviewDialogOpen(true);
  };

  const handleReviewComplete = (updatedStatement) => {
    // Refresh the list after review
    loadStatements();
    setReviewDialogOpen(false);
    setSelectedStatement(null);
  };

  const getStatusBackgroundColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'rgba(33, 150, 243, 0.08)'; // info blue
      case 'under_review':
        return 'rgba(255, 152, 0, 0.08)'; // warning orange
      case 'finalized':
        return 'rgba(76, 175, 80, 0.08)'; // success green
      case 'rejected':
        return 'rgba(244, 67, 54, 0.08)'; // error red
      default:
        return 'transparent';
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'rgba(33, 150, 243, 0.3)'; // info blue
      case 'under_review':
        return 'rgba(255, 152, 0, 0.3)'; // warning orange
      case 'finalized':
        return 'rgba(76, 175, 80, 0.3)'; // success green
      case 'rejected':
        return 'rgba(244, 67, 54, 0.3)'; // error red
      default:
        return 'transparent';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleMouseDown = (columnKey) => (e) => {
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Typography variant="h5">
          Statement Review Queue
        </Typography>
        <Button variant="outlined" onClick={loadStatements}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {statements.length === 0 ? (
        <Card sx={{ flexShrink: 0 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No statements pending review
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <ResizableTableCell width={columnWidths.incidentRef} onResize={handleMouseDown('incidentRef')}>
                    Incident Ref
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.statementId} onResize={handleMouseDown('statementId')}>
                    Statement ID
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.email} onResize={handleMouseDown('email')}>
                    Email
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.handle} onResize={handleMouseDown('handle')}>
                    Handle
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.accountId} onResize={handleMouseDown('accountId')}>
                    Account ID
                  </ResizableTableCell>
                  <ResizableTableCell width={columnWidths.submittedAt} onResize={handleMouseDown('submittedAt')}>
                    Submitted At
                  </ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statements.map((statement) => (
                  <TableRow
                    key={statement.statement_id}
                    onClick={() => handleOpenReview(statement)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: getStatusBackgroundColor(statement.status),
                      borderLeft: `4px solid ${getStatusBorderColor(statement.status)}`,
                      '&:hover': {
                        opacity: 0.8,
                        filter: 'brightness(0.95)'
                      }
                    }}
                  >
                    <ResizableTableCell width={columnWidths.incidentRef}>
                      {statement.incident_ref || '-'}
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.statementId}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {statement.statement_id.substring(0, 12)}...
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.email}>
                      {statement.email || '-'}
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.handle}>
                      {statement.created_by_handle || '-'}
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.accountId}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {statement.account_id || '-'}
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.submittedAt}>
                      {formatDate(statement.submitted_at)}
                    </ResizableTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Review Dialog */}
      <StatementReviewDialog
        open={reviewDialogOpen}
        onClose={() => {
          setReviewDialogOpen(false);
          setSelectedStatement(null);
        }}
        statement={selectedStatement}
        onReviewComplete={handleReviewComplete}
      />
    </Box>
  );
};

export default StatementReviewQueue;
